import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Globe } from 'lucide-react';
import { fmt } from '@/pages/Dashboard';
import type { PageviewDomainData } from '@/types/pageview-report';

type SortKey = keyof Pick<
    PageviewDomainData,
    'pageviews' | 'unique_sessions' | 'revenue' | 'impressions' | 'rps' | 'rpm' | 'ipvu'
>;

interface Props {
    data: PageviewDomainData[];
}

const COLUMNS: { key: SortKey; label: string; hint?: string }[] = [
    { key: 'pageviews', label: 'Pageviews' },
    { key: 'unique_sessions', label: 'Sessões' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'impressions', label: 'Impr.' },
    { key: 'rps', label: 'RPS', hint: 'Revenue por sessão' },
    { key: 'rpm', label: 'RPM', hint: 'Revenue por mil PV' },
    { key: 'ipvu', label: 'IPVU', hint: 'Impressões por visita única' },
];

export function DomainsTab({ data }: Props) {
    const [sortKey, setSortKey] = useState<SortKey>('pageviews');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const sorted = useMemo(() => {
        return [...data].sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            return sortDir === 'desc' ? bv - av : av - bv;
        });
    }, [data, sortKey, sortDir]);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    if (data.length === 0) {
        return (
            <EmptyState message="Nenhum dado por domínio no período selecionado" />
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900">
                        Métricas por domínio
                    </h3>
                </div>
                <span className="text-xs text-gray-500">{data.length} domínios</span>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Domínio
                            </th>
                            {COLUMNS.map((c) => (
                                <SortableTh
                                    key={c.key}
                                    label={c.label}
                                    hint={c.hint}
                                    active={sortKey === c.key}
                                    dir={sortDir}
                                    onClick={() => toggleSort(c.key)}
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map((d) => (
                            <tr key={d.domain} className="hover:bg-gray-50">
                                <td className="px-4 py-2.5 font-medium text-gray-900 max-w-[260px] truncate">
                                    {d.domain}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-700">
                                    {fmt.number(d.pageviews)}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-700">
                                    {fmt.number(d.unique_sessions)}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-emerald-700 font-medium">
                                    {fmt.currency(d.revenue)}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-700">
                                    {fmt.number(d.impressions)}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                    {fmt.currency(d.rps)}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                    {fmt.currency(d.rpm)}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                    {d.ipvu.toFixed(2)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ------- Reutilizavel -------

export function SortableTh({
    label,
    hint,
    active,
    dir,
    onClick,
}: {
    label: string;
    hint?: string;
    active: boolean;
    dir: 'asc' | 'desc';
    onClick: () => void;
}) {
    return (
        <th
            title={hint}
            className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500"
        >
            <button
                onClick={onClick}
                className={`inline-flex items-center gap-1 transition-colors ${
                    active ? 'text-gray-900' : 'hover:text-gray-700'
                }`}
            >
                {label}
                {active ? (
                    dir === 'desc' ? (
                        <ArrowDown className="h-3 w-3" />
                    ) : (
                        <ArrowUp className="h-3 w-3" />
                    )
                ) : (
                    <ArrowUpDown className="h-3 w-3 opacity-40" />
                )}
            </button>
        </th>
    );
}

export function EmptyState({ message }: { message: string }) {
    return (
        <div className="rounded-lg border border-dashed border-gray-200 bg-white p-12 text-center">
            <p className="text-sm text-gray-500">{message}</p>
        </div>
    );
}
